import React, { useState, useEffect, useMemo } from "react";
import { Button } from "./Button";
import Input from "./Input";
import arrowImage from "../assets/svg/arrow.svg";
import { useSelector } from "react-redux";
import useLocale from "../locales";
import { useFormik } from "formik";
import * as Yup from "yup";
import { languageList, HIGH_VALUE_CURRENCIES } from "../constants";

const prepareGoalSubmission = (values, timeUnit, settings, textData) => {
  const { language, currency, country, numberSystem } = settings;

  const target = parseFloat(values.targetAmount) || 0;
  const timeframeValue = parseFloat(values.timeframe) || 1;
  const months = timeUnit === textData.goalPlanner.years ? timeframeValue * 12 : timeframeValue;
  const current = parseFloat(values.currentSavings) || 0;
  const monthly = parseFloat(values.monthlyContribution) || 0;

  const remainingAmount = target - current;
  const totalContributions = monthly * months;
  const shortfall = remainingAmount - totalContributions;
  const requiredMonthly = remainingAmount / months;

  const payload = {
    language: `${language.value}-${language.label}`,
    locale_context: country.value,
    number_system: numberSystem,
    currency: currency.value,
    goal_name: values.goalName,
    type: "custom_goal",
    target_amount: target,
    current_savings: current,
    time_period: `${timeframeValue} ${timeUnit}`,
    country: country.value,
  };

  const calculationResult = {
    targetAmount: target,
    currentSavings: current,
    remainingAmount,
    timeframeMonths: months,
    timeframeDisplay: timeframeValue,
    timeUnit,
    monthlyContribution: monthly,
    totalContributions,
    requiredMonthly,
    shortfall,
    isAchievable: shortfall <= 0,
  };

  return { payload, calculationResult };
};

const OtherGoalCalculator = ({ onSubmit, initialValues, initialTimeUnit }) => {
  const { textData } = useLocale();
  const [timeUnit, setTimeUnit] = useState(initialTimeUnit || textData.goalPlanner.years);

  const { language, currency, country, numberSystem } = useSelector((state) => state.settings);

  const targetAmountBounds = useMemo(() => {
    const isHighValue = HIGH_VALUE_CURRENCIES.includes(currency?.value || "");
    return isHighValue ? { min: 1000, max: 1000000, step: 1000 } : { min: 10000, max: 10000000, step: 10000 };
  }, [currency?.value]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        goalName: Yup.string()
          .trim()
          .required(textData.goalPlanner.enterGoalName || "Goal name is required")
          .max(50, textData.goalPlanner.goalNameMaxLength || "Goal name must be less than 50 characters"),
        targetAmount: Yup.number()
          .min(
            targetAmountBounds.min,
            (textData.goalPlanner.amountMin && textData.goalPlanner.amountMin.replace("{min}", targetAmountBounds.min.toLocaleString())) ||
              `Amount must be at least ${targetAmountBounds.min.toLocaleString()}`
          )
          .required(textData.goalPlanner.targetAmountRequired || "Target amount is required")
          .max(
            targetAmountBounds.max,
            textData.goalPlanner.targetAmountMax.replace("{max}", targetAmountBounds.max.toLocaleString()) ||
              `Target amount must be at most ${targetAmountBounds.max.toLocaleString()}`
          ),
        timeframe: Yup.number()
          .min(1, textData.goalPlanner.timePeriodMin || "Time period must be greater than 0")
          .required(textData.goalPlanner.timePeriodRequired || "Time period is required")
          .test(
            "max-timeframe",
            timeUnit === textData.goalPlanner.years
              ? textData.goalPlanner.yearsMaxError(7) || "Years cannot exceed 7"
              : textData.goalPlanner.monthsMaxError(84) || "Months cannot exceed 84",
            (value) => {
              if (value == null || value === "") return true;
              const num = Number(value);
              return timeUnit === textData.goalPlanner.years ? num <= 7 : num <= 84;
            }
          ),
        currentSavings: Yup.number()
          .min(0, textData.goalPlanner.currentSavingsMin || "Current savings cannot be negative")
          .default(0)
          .max(targetAmountBounds.max, textData.goalPlanner.currentSavingsMax || `Current savings must be less than ${(targetAmountBounds.max + 1).toLocaleString()}`)
          .test(
            "less-than-target",
            textData.goalPlanner.targetAmountHigherThanSavings || "Target amount must be higher than current savings",
            function (value) {
              const { targetAmount } = this.parent;
              return (value || 0) < (targetAmount || 0);
            }
          ),
      }),
    [timeUnit, textData, targetAmountBounds]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues || {
      goalName: "",
      targetAmount: 10000,
      timeframe: 1,
      currentSavings: 0,
      monthlyContribution: 0,
    },
    validationSchema,
    onSubmit: (values) => {
      const settings = { language, currency, country, numberSystem };
      const submission = prepareGoalSubmission(values, timeUnit, settings, textData);
      onSubmit({ ...submission, values, timeUnit });
    },
  });

  useEffect(() => {
    if (initialTimeUnit) {
      setTimeUnit(initialTimeUnit);
    }
  }, [initialTimeUnit]);

  useEffect(() => {
    const v = formik.values.targetAmount;
    if (v > targetAmountBounds.max || v < targetAmountBounds.min) {
      formik.setFieldValue("targetAmount", Math.min(targetAmountBounds.max, Math.max(targetAmountBounds.min, v)));
    }
  }, [currency?.value]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numericValue = value.replace(/[^0-9]/g, "");
    formik.setFieldValue(name, numericValue ? parseFloat(numericValue) : "");
    if (name === "currentSavings" || name === "targetAmount") {
      formik.validateField("currentSavings");
    }
  };

  const toggleTimeUnit = () => {
    const currentValue = formik.values.timeframe;
    if (timeUnit === textData.goalPlanner.years) {
      setTimeUnit(textData.goalPlanner.months);
      const months = Math.min(6, currentValue * 12);
      formik.setFieldValue("timeframe", months);
    } else {
      setTimeUnit(textData.goalPlanner.years);
      const years = Math.min(50, Math.max(1, Math.round(currentValue / 12)));
      formik.setFieldValue("timeframe", years);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return "";
    return parseFloat(value).toLocaleString("en-US");
  };

  const currentCurrency = useMemo(() => {
    return languageList.find((lang) => lang.currencyCode === currency.value)?.currency || currency.symbol;
  }, [currency]);

  const handlePlanMyGoal = (e) => {
    e.preventDefault();
    const target = Number(formik.values.targetAmount) || 0;
    const savings = Number(formik.values.currentSavings) || 0;
    if (target <= savings) {
      formik.setFieldError(
        "currentSavings",
        textData.goalPlanner.targetAmountHigherThanSavings || "Target amount must be higher than current savings"
      );
      formik.setFieldTouched("currentSavings", true);
      return;
    }
    formik.handleSubmit();
  };

  return (
    <form onSubmit={handlePlanMyGoal} className="w-full space-y-4">
      <div className="flex flex-col gap-8">
        <div className="flex flex-col flex-wrap">
          <div className="flex gap-2">
            <span className="text-lg text-[#282828] font-lato-regular-400">{textData.goalPlanner.wantSave}</span>
            <div className="flex flex-col min-w-[160px]">
              <Input
                leftIcon={currentCurrency}
                type="text"
                name="targetAmount"
                value={formatCurrency(formik.values.targetAmount)}
                onChange={handleInputChange}
                onBlur={formik.handleBlur}
                placeholder={textData.goalPlanner.enterAmount}
                className="flex-1 outline-none bg-transparent text-[#282828] font-lato-regular-400 placeholder:text-gray-400"
              />
              {formik.touched.targetAmount && formik.errors.targetAmount && (
                <p className="text-red-500 text-xs mt-2 ml-2">{formik.errors.targetAmount}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="flex gap-2 flex-wrap">
            <span className="text-lg text-[#282828] font-lato-regular-400">{textData.goalPlanner.for}</span>
            <div className="flex flex-col min-w-[180px]">
              <Input
                type="text"
                name="goalName"
                value={formik.values.goalName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={textData.goalPlanner.enterGoalName}
                className="flex-1 outline-none bg-transparent text-[#282828] font-lato-regular-400 placeholder:text-gray-400"
              />
              {formik.touched.goalName && formik.errors.goalName && (
                <p className="text-red-500 text-xs mt-2 ml-2">{formik.errors.goalName}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="flex gap-2 flex-wrap">
            <div className="flex gap-2 items-start">
              <span className="text-lg text-[#282828] font-lato-regular-400">{textData.goalPlanner.in}</span>
              <div
                onClick={toggleTimeUnit}
                className="cursor-pointer h-9 flex items-center gap-1 text-primary hover:text-primary-dark text-base font-lato-semibold-600 transition"
              >
                {timeUnit}
                <img src={arrowImage} alt="Dropdown arrow" className="w-3 h-3" />
              </div>
            </div>
            <div className="flex flex-col min-w-[140px]">
              <Input
                leftIcon={timeUnit === textData.goalPlanner.years ? "Yr" : "M"}
                type="number"
                min={1}
                name="timeframe"
                value={formik.values.timeframe}
                onChange={handleInputChange}
                onBlur={formik.handleBlur}
                placeholder={textData.goalPlanner.enterTimePeriod}
                className="flex-1 outline-none bg-transparent text-[#282828] font-lato-regular-400 placeholder:text-gray-400"
              />
              {formik.touched.timeframe && formik.errors.timeframe && (
                <p className="text-red-500 text-xs mt-2 ml-2">{formik.errors.timeframe}</p>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-t-[#DDE1E5] h-[1px] w-full" />
        <Button type="submit" className="w-full rounded-full font-lato-semibold-600 text-base transition-colors">
          {textData.goalPlanner.planBtn}
        </Button>
      </div>
    </form>
  );
};

export default OtherGoalCalculator;
